// Recordings: pick the best recorder format, and turn a finished clip into a
// public share page (/r/<id>) — video + poster go to R2 under the user's own
// folder, then create_recording_share() makes the row and returns the slug.
import { supabase } from '../config/supabase';
import { putObject } from '../utils/uploadToStorage';

/** MP4 plays everywhere (iPhone, social apps); fall back to WebM where MP4 recording isn't supported. */
export function pickRecordingMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1,opus',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm',
  ].find((t) => MediaRecorder.isTypeSupported(t));
}

export const baseMime = (mime: string | undefined) => (mime && mime.startsWith('video/mp4') ? 'video/mp4' : 'video/webm');
export const extFor = (mime: string) => (baseMime(mime) === 'video/mp4' ? 'mp4' : 'webm');

export interface FinishedRecording {
  blob: Blob;
  mime: string;          // base type: video/mp4 | video/webm
  durationS: number;
  worldId?: string | null;
  worldName?: string;
}

export const sharePageUrl = (id: string) => `${window.location.origin}/r/${id}`;

/** Grab a still from the clip for the share card (skips the first, often black, frame). */
export async function posterFrom(blob: Blob): Promise<Blob | null> {
  const url = URL.createObjectURL(blob);
  try {
    const v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.preload = 'auto'; v.src = url;
    await new Promise<void>((res, rej) => { v.onloadeddata = () => res(); v.onerror = () => rej(new Error('video')); setTimeout(() => rej(new Error('timeout')), 8000); });
    const t = Number.isFinite(v.duration) && v.duration > 1.5 ? Math.min(1.2, v.duration / 3) : 0.3;
    await new Promise<void>((res) => { v.onseeked = () => res(); v.currentTime = t; setTimeout(res, 2000); });
    const w = v.videoWidth || 1280, h = v.videoHeight || 720, scale = Math.min(1, 1280 / w);
    const c = document.createElement('canvas'); c.width = Math.round(w * scale); c.height = Math.round(h * scale);
    c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
    return await new Promise((res) => c.toBlob((b) => res(b), 'image/jpeg', 0.86));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function shareRecording(rec: FinishedRecording, onProgress?: (pct: number) => void): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to share your recording.');
  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const poster = await posterFrom(rec.blob);
  const videoUrl = await putObject(`recordings/${user.id}/${stamp}.${extFor(rec.mime)}`, rec.blob, rec.mime, {
    cacheControl: 'public, max-age=31536000, immutable',
    onProgress: (p) => onProgress?.(Math.round(p * 0.95)),
  });
  const posterUrl = poster
    ? await putObject(`recordings/${user.id}/${stamp}.jpg`, poster, 'image/jpeg', { cacheControl: 'public, max-age=31536000, immutable' }).catch(() => null)
    : null;
  const { data, error } = await supabase.rpc('create_recording_share', {
    p_world_id: rec.worldId || null,
    p_title: rec.worldName ? `In ${rec.worldName}` : null,
    p_video_url: videoUrl,
    p_poster_url: posterUrl,
    p_mime_type: rec.mime,
    p_duration_s: rec.durationS,
  });
  if (error) throw new Error(error.message);
  onProgress?.(100);
  return data as string;
}

export interface RecordingShare {
  id: string; title: string | null; video_url: string; poster_url: string | null; mime_type: string;
  duration_s: number | null; created_at: string; views: number;
  world_id: string | null; world_name: string | null; world_thumbnail: string | null; world_public: boolean;
  creator_name: string | null; creator_username: string | null;
}

export async function getRecordingShare(id: string, countView = false): Promise<RecordingShare | null> {
  const { data, error } = await supabase.rpc('get_recording_share', { p_id: id, p_count_view: countView });
  if (error) throw new Error(error.message);
  return (data as RecordingShare) || null;
}
